import { TopoNode } from "../Topology/TopoNode";
import { DeviceKey, IfKey, KeyNames } from "../Core/Keys";
import { DeviceGroupMember } from "../Core/DeviceGroupMember";
import { TopoImageKey, TopoImageMap } from "../Topology/TopoImageMap";
import { TopoImageLibrary } from "../Topology/TopoImageLibrary";
import { Device } from "../Core/Device";
import { DeviceCategory } from "../Core/DeviceCategory";
import { DeviceCategories } from "../Core/DeviceCategories";
import { VendorNames } from "../InfoSets/ProductInfo";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";

export enum DeviceState {
	Unknown = 0,
	Down,
	Warning,
	Information,
	Up,
	Paused,
	NoData
}

export class DeviceNode extends TopoNode implements ObjectSerializer {
	private _key: DeviceKey;
	private _deviceGroupMember: DeviceGroupMember;
	private _nodeImage: HTMLImageElement;
	private _nodeImageKey: TopoImageKey;
	private _vendorImage: HTMLImageElement;
	private _vendorImageKey: TopoImageKey;
	private _stateIconImage: HTMLImageElement;
	private _stateBackgroundImage: HTMLImageElement;

	private _curState: DeviceState;

	public get DeviceState(): DeviceState {
		return this._curState;
	}
	public set DeviceState(value: DeviceState) {
		this._curState = value;

		var iconImageKey = new TopoImageKey();

		switch (this.DeviceState) {
			case DeviceState.Down:
				iconImageKey.Status = "DownIcon";
				break;
			case DeviceState.Up:
				iconImageKey.Status = "UpIcon";
				break;
			case DeviceState.Warning:
				iconImageKey.Status = "WarnIcon";
				break;
			case DeviceState.Unknown:
				iconImageKey.Status = "UnknownIcon";
				break;
			default:
				iconImageKey.Status = '';
				break;
		}

		this._stateIconImage = TopoImageLibrary.FindImage(iconImageKey);

		var backgroundImageKey = new TopoImageKey();

		switch (this.DeviceState) {
			case DeviceState.Down:
				backgroundImageKey.Status = "DownBackground";
				break;
			case DeviceState.Warning:
				backgroundImageKey.Status = "WarnBackground";
				break;
			case DeviceState.Unknown:
				backgroundImageKey.Status = "UnknownBackground";
				break;
			default:
				backgroundImageKey.Status = '';
				break;
		}

		this._stateBackgroundImage = TopoImageLibrary.FindImage(backgroundImageKey);
	}

	public get DeviceGroupMember(): DeviceGroupMember {
		return this._deviceGroupMember;
	}
	public set DeviceGroupMember(value: DeviceGroupMember) {
		this._deviceGroupMember = value;
		if (this._deviceGroupMember != null) {
			this._key = this._deviceGroupMember.Key;
			this.InitImages();
			this.SetSize();
		}
		else {
			this._key = null;
			this.ClearImages();
		}
	}

	public get HasDeviceGroupMember(): boolean {
		return !!this.DeviceGroupMember;
	}

	public get Key(): DeviceKey {
		return this._key;
	}

	public get HasKey(): boolean {
		return !!this.Key;
	}
				
	public get Device(): Device {
		if (this.HasDeviceGroupMember)
			return this.DeviceGroupMember.Device;
		else
			return null;
	}

	public get HasDevice(): boolean {
		return !!this.Device;
	}

	constructor(member: DeviceGroupMember = null) {
		super();
		this.DeviceGroupMember = member;
		this.DeviceState = DeviceState.NoData;
	}

	public get Label(): string {
		if (this.HasDeviceGroupMember) {
			return this.DeviceGroupMember.DisplayName;
		}
		else {
			this.Key.ForEach((key) => {
				if (key.Name.indexOf(KeyNames.SysName) == 0)
					return key.Value;
			});

			this.Key.ForEach((key) => {
				if (key.Name.indexOf(KeyNames.Ip) == 0)
					return key.Value;
			});

			return '';
		}
	}

	public get NodeImage():HTMLImageElement {
		return this._nodeImage;
	}

	public get VendorImage():HTMLImageElement {
		return this._vendorImage;
	}

	public get StatusImage(): HTMLImageElement {
		return this._stateIconImage;
	}

	public get StatusBackground(): HTMLImageElement {
		return this._stateBackgroundImage;
	}

	public Resolve(member: DeviceGroupMember):void {
		this.DeviceGroupMember = member;
	}

	public ResolveNode(node: TopoNode): void {
		var deviceNode: DeviceNode = <DeviceNode> node;
		if (deviceNode != null) {
			this.DeviceGroupMember = deviceNode.DeviceGroupMember;
		}
	}

	public UnResolve():void {
		this._deviceGroupMember = null;
	}

	private ClearImages():void {
		this._nodeImage = null;
		this._nodeImageKey = null;
		this._vendorImage = null;
		this._vendorImageKey = null;
	}

	private SetSize():void {
		var nodeImage: HTMLImageElement = this.NodeImage;
		if (nodeImage != null) {
			this.Width = nodeImage.width;
			this.Height = nodeImage.height;
		}
	}

	public get DisplayName(): string {
		if (this.Device)
			return this.Device.DisplayName;
		else
			return "Device Node";
	}

	public get Image(): HTMLImageElement {
		return this.NodeImage;
	}

	private InitImages(): void {
		if (this.HasDevice || this.HasDeviceGroupMember) {
			let vendor: string = "";
			if (this.HasDevice && this.Device.HasProductInfo && this.Device.ProductInfo.HasVendor)
				vendor = this.Device.ProductInfo.Vendor;
			if (!vendor && this.HasDeviceGroupMember)
				vendor = this.DeviceGroupMember.Vendor;

			let categories: DeviceCategories = null;
			if (this.HasDevice)
				categories = this.Device.Categories;
			if (!categories && this.HasDeviceGroupMember)
				categories = this.DeviceGroupMember.Categories;
			if (!categories)
				categories = new DeviceCategories();

			this._vendorImageKey = new TopoImageKey();

			if (vendor)
				this._vendorImageKey.Vendor = vendor;

			if (this._vendorImageKey.Vendor)
				this._vendorImage = TopoImageLibrary.FindImage(this._vendorImageKey);

			if (this._vendorImage == null) {
				if (categories.Contains(DeviceCategory.Windows))
					this._vendorImageKey.Vendor = VendorNames.Microsoft;
				else if (categories.Contains(DeviceCategory.WindowsServer))
					this._vendorImageKey.Vendor = VendorNames.Microsoft;
				else if (categories.Contains(DeviceCategory.Apple))
					this._vendorImageKey.Vendor = VendorNames.Apple;
				else
					this._vendorImageKey.Vendor = '';

				if (this._vendorImageKey.Vendor)
					this._vendorImage = TopoImageLibrary.FindImage(this._vendorImageKey);
			}

			if (this._vendorImage == null)
				this._vendorImageKey = null;

			this._nodeImageKey = new TopoImageKey();
			var primaryCategory: DeviceCategory = categories.PrimaryDeviceCategory;
			if (primaryCategory != null)
				this._nodeImageKey.Category = primaryCategory;
			else
				this._nodeImageKey.AddEmptyCat();
		}

		this._nodeImage = TopoImageLibrary.FindImage(this._nodeImageKey);
		if (this._nodeImage == null) {
			this._nodeImage = new Image(48, 48);
		}
    }

	public Write(obj: any, context: any): void {
		obj.device_node = {};
		obj = obj.device_node;

		super.Write(obj, context);

		if (!!this._key)
			this._key.Write(obj, context);

		if (!!this._nodeImageKey)
			this._nodeImageKey.Write(obj, "node_image_key", context);

		if (!!this._vendorImageKey)
			this._vendorImageKey.Write(obj, "vendor_image_key", context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.device_node)
			throw new Error();
		obj = obj.device_node;

		super.Read(obj, context);

		if (obj.device_key !== undefined) {
			this._key = new DeviceKey();
			this._key.Read(obj, context);
		}
		if (obj.node_image_key !== undefined) {
			this._nodeImageKey = new TopoImageKey();
			this._nodeImageKey.Read(obj, "node_image_key", context);
		}
		if (obj.vendor_image_key !== undefined) {
			this._vendorImageKey = new TopoImageKey();
			this._vendorImageKey.Read(obj, "vendor_image_key", context);
		}
	}

}
